import {
  AlertTriangle,
  Building2,
  Check,
  Droplets,
  Sparkles,
  TrendingUp,
  User,
} from "lucide-react";
import { toast } from "sonner";
import ClassroomsToiletsGuidanceChip from "@/components/ClassroomsToiletsGuidanceChip";
import HeroStatusChip from "@/components/HeroStatusChip";
import InterventionActionPlan from "@/components/InterventionActionPlan";
import { asInsightArray, cardsToInsightLines, toInsightLines } from "@/utils/insightActionPlanDefaults";
import { buildClassroomsToiletsLocalGuidance } from "@/utils/buildClassroomsToiletsLocalGuidance";
import { toTitleCase } from "../utils/toTitleCase";

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? 0);
const pct = (v, digits = 1) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "0%");

const buildGaugeStyle = (value, color = "var(--ct-accent)") => ({
  background: `radial-gradient(circle at center, white 0 52%, transparent 53%), conic-gradient(${color} 0 ${Math.min(value, 100)}%, rgba(20,184,166,.14) ${Math.min(value, 100)}% 100%)`,
});

const buildConfidenceGaugeStyle = (value) => ({
  background: `radial-gradient(circle at center, #fff 0 52%, transparent 53%), conic-gradient(var(--ct-primary) 0 ${Math.min(value, 100)}%, rgba(37,99,235,.10) ${Math.min(value, 100)}% 100%)`,
});

const computeAiReadiness = (overview) => {
  if (!overview) return 0;
  if (overview.infrastructure_readiness_index != null) {
    return Math.round(overview.infrastructure_readiness_index * 10) / 10;
  }
  return (
    Math.round(
      ((overview.classroom_health_index ?? 0) +
        (overview.toilet_functional_pct ?? 0) +
        (overview.water_coverage_pct ?? 0)) /
        3
    ) * 10
  ) / 10;
};

const avg = (...values) => {
  const nums = values.filter((v) => typeof v === "number");
  if (!nums.length) return 0;
  return nums.reduce((a, b) => a + b, 0) / nums.length;
};

const computeConfidence = (overview, blockData) => {
  const total = overview?.total_schools ?? 0;
  if (!total) return 0;
  const metricAvg = avg(
    overview.classroom_health_index ?? 0,
    overview.toilet_functional_pct ?? 0,
    overview.water_coverage_pct ?? 0
  );
  const blockScores = (blockData ?? []).map((b) => b.infrastructure_readiness ?? b.readiness_score ?? 0).filter((v) => v > 0);
  const spread = blockScores.length > 1 ? Math.max(...blockScores) - Math.min(...blockScores) : 0;
  const cohesion = Math.max(0, 100 - spread);
  return Math.round(avg(metricAvg, cohesion));
};

const deriveTimelineDays = (gapCount, totalSchools) => {
  if (!gapCount || !totalSchools) return 30;
  const ratio = gapCount / totalSchools;
  if (ratio > 0.25) return 15;
  if (ratio > 0.1) return 30;
  if (ratio > 0.03) return 45;
  return 60;
};

const countCriticalThemes = (signals) =>
  signals.filter((s) => s.tone === "red" || s.tone === "warn").length;

const BriefCard = ({ icon: Icon, iconTone, title, value, sub, description }) => (
  <article className="ct-brief-card">
    <div className={`ct-icon-box ${iconTone}`}>
      <Icon size={22} />
    </div>
    <h3>{toTitleCase(title)}</h3>
    <div className="ct-brief-value">
      <strong>{value}</strong>
      <span>{sub}</span>
    </div>
    <p>{description}</p>
  </article>
);

const SignalRow = ({ icon: Icon, iconTone, title, description, pillLabel, pillTone = "" }) => (
  <div className="ct-signal-row">
    <div className={`ct-icon-box ${iconTone}`}>
      <Icon size={22} />
    </div>
    <div>
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
    <span className={`ct-score-pill ${pillTone}`}>{pillLabel}</span>
  </div>
);

const SIGNAL_ICON_MAP = {
  warn: { icon: Droplets, iconTone: "orange", pillTone: "warn" },
  red: { icon: Building2, iconTone: "red", pillTone: "red" },
  good: { icon: User, iconTone: "green", pillTone: "" },
};

const enrichDiagnosticSignals = (signals) =>
  (signals ?? []).map((signal) => {
    const meta = SIGNAL_ICON_MAP[signal.tone] ?? SIGNAL_ICON_MAP.warn;
    return {
      ...meta,
      title: signal.title,
      description: signal.description,
      pillLabel: signal.pillLabel ?? signal.pill_label ?? "Monitor",
      pillTone: signal.pillTone ?? meta.pillTone,
      tone: signal.tone,
    };
  });

const ClassroomsToiletsInsightsTab = ({
  overview,
  riskSchools,
  equityMetrics,
  hygieneMetrics,
  blockData,
  guidance: guidanceProp,
  guidanceProvider = "local",
  guidanceLoading = false,
  guidanceGeneratedAt,
}) => {
  if (!overview) {
    return (
      <div className="ct-empty-card">
        <Sparkles className="mx-auto mb-3 opacity-40" size={40} />
        <p>No insights data available for the selected scope.</p>
      </div>
    );
  }

  const riskSummary = riskSchools?.risk_summary ?? {};
  const aiReadiness = computeAiReadiness(overview);
  const confidence = computeConfidence(overview, blockData);
  const localGuidance = buildClassroomsToiletsLocalGuidance({
    overview,
    riskSchools,
    equityMetrics,
    hygieneMetrics,
    blockData,
  });
  const insightsGuidance = guidanceProp ?? localGuidance?.insights;

  const generatedAt = guidanceGeneratedAt
    ? new Date(guidanceGeneratedAt).toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })
    : new Date().toLocaleString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

  const zeroBoys = overview.zero_boys_toilet_schools ?? 0;
  const zeroGirls = overview.zero_girls_toilet_schools ?? 0;
  const zeroToiletTotal = riskSummary.zero_toilet_schools ?? zeroBoys + zeroGirls;
  const waterGap = overview.water_gap ?? 0;
  const handwashPoints = overview.handwash_points ?? hygieneMetrics?.handwash_points ?? 0;
  const sufficiency = overview.handwash_sufficiency_ratio ?? hygieneMetrics?.handwash_sufficiency ?? 0;
  const cwsnCoverage = equityMetrics?.cwsn_coverage_pct ?? 0;
  const cwsnGap = Math.round((100 - cwsnCoverage) * 10) / 10;
  const majorRepairSchools = riskSummary.major_repair_schools ?? 0;
  const majorRepairClassrooms = overview.classrooms_major_repair ?? 0;

  const criticalExceptions =
    (riskSummary.zero_toilet_schools ?? 0) +
    (riskSummary.no_water_schools ?? 0) +
    (riskSummary.no_handwash_schools ?? 0) +
    (riskSummary.major_repair_schools ?? 0);

  const localDiagnosticSignals = [
    {
      icon: Droplets,
      iconTone: "orange",
      title: "Water supply is the main usability constraint",
      description: `Toilet functionality is high, but ${fmt(waterGap)} toilets are not fully usable due to lack of water.`,
      pillLabel: "High",
      pillTone: "warn",
      tone: "warn",
    },
    {
      icon: Building2,
      iconTone: "red",
      title: "Major repair backlog needs safety prioritisation",
      description: `${fmt(majorRepairSchools)} schools require major repairs, with ${fmt(majorRepairClassrooms)} classrooms needing urgent repair attention.`,
      pillLabel: "Critical",
      pillTone: "red",
      tone: "red",
    },
    {
      icon: Droplets,
      iconTone: "purple",
      title: "Handwash sufficiency is below ideal",
      description: `${fmt(handwashPoints)} handwash points produce a sufficiency ratio of ${sufficiency.toFixed(2)}, below the ideal 1.0 benchmark.`,
      pillLabel: "Medium",
      pillTone: "warn",
      tone: "warn",
    },
    {
      icon: User,
      iconTone: "green",
      title: "Inclusion readiness is good but not complete",
      description: `CWSN school coverage is ${pct(cwsnCoverage)}, leaving a ${pct(cwsnGap)} coverage gap that needs targeted closure.`,
      pillLabel: "Good",
      pillTone: "",
      tone: "good",
    },
  ];

  const diagnosticSignals = enrichDiagnosticSignals(
    asInsightArray(insightsGuidance?.diagnostic_signals, localDiagnosticSignals)
  );

  const criticalThemes = countCriticalThemes(diagnosticSignals);

  const totalSchools = overview.total_schools ?? 1;
  const actionFocusDays = insightsGuidance?.action_focus_days ?? deriveTimelineDays(criticalExceptions, totalSchools);
  const primaryExecutiveSignal =
    insightsGuidance?.primary_signal ??
    diagnosticSignals.find((s) => s.tone === "red")?.description ??
    diagnosticSignals.find((s) => s.tone === "warn")?.description ??
    diagnosticSignals[0]?.description ??
    "";
  const insightsHeroSummary =
    insightsGuidance?.hero_summary ??
    `Infrastructure readiness is ${pct(aiReadiness)} across ${fmt(totalSchools)} schools with ${fmt(criticalExceptions)} critical WASH exceptions (${fmt(waterGap)} water gap, ${fmt(zeroToiletTotal)} zero-toilet schools).`;
  const noWaterSchools = riskSummary.no_water_schools ?? 0;
  const noHandwashSchools = riskSummary.no_handwash_schools ?? 0;
  const localClosurePlan = [
    {
      num: "01",
      title: "Create verified exception list",
      p: `Freeze ${fmt(zeroToiletTotal)} zero-toilet, ${fmt(noWaterSchools)} no-water, ${fmt(noHandwashSchools)} no-handwash and ${fmt(majorRepairSchools)} major-repair schools for closure tracking.`,
    },
    {
      num: "02",
      title: "Assign block owners",
      p: `Allocate owners across ${fmt(overview.total_blocks ?? 0)} blocks for ${fmt(criticalExceptions)} combined risk exceptions with weekly status updates.`,
    },
    {
      num: "03",
      title: "Prioritise safety-first repairs",
      p: `Close ${fmt(majorRepairClassrooms)} major-repair classrooms in ${fmt(majorRepairSchools)} schools before usability fixes.`,
    },
    {
      num: "04",
      title: "Review with evidence",
      p: `Target ${pct(overview.toilet_functional_pct)} toilet functionality and ${pct(overview.water_coverage_pct)} water coverage with verified school records.`,
    },
  ];
  const statusFromRatio = (ratio) => {
    if (ratio > 0.2) return { status: "Critical", statusTone: "danger" };
    if (ratio > 0.08) return { status: "High", statusTone: "danger" };
    if (ratio > 0.02) return { status: "Medium", statusTone: "warn" };
    return { status: "Monitor", statusTone: "blue" };
  };

  const localActionQueue = [
    {
      priority: 1,
      area: "Zero-toilet schools",
      detail: "Schools with missing boys/girls toilets",
      signal: `${fmt(zeroToiletTotal)} schools`,
      owner: "Block Engineer + BEO",
      timeline: `0-${deriveTimelineDays(zeroToiletTotal, totalSchools)} days`,
      ...statusFromRatio(zeroToiletTotal / totalSchools),
    },
    {
      priority: 2,
      area: "Toilet water gap",
      detail: "Functional toilets lacking water supply",
      signal: `${fmt(waterGap)} toilets`,
      owner: "WASH Officer",
      timeline: `0-${deriveTimelineDays(waterGap, overview.total_toilets ?? (waterGap || 1))} days`,
      ...statusFromRatio(waterGap / Math.max(overview.total_toilets ?? 1, 1)),
    },
    {
      priority: 3,
      area: "Major classroom repairs",
      detail: "Safety and usability repair backlog",
      signal: `${fmt(majorRepairClassrooms)} classrooms`,
      owner: "Infrastructure Cell",
      timeline: `0-${deriveTimelineDays(majorRepairSchools, totalSchools)} days`,
      ...statusFromRatio(majorRepairSchools / totalSchools),
    },
    {
      priority: 4,
      area: "Handwash sufficiency",
      detail: "Points per toilet below ideal ratio",
      signal: `${sufficiency.toFixed(2)} ratio`,
      owner: "School Head + Cluster",
      timeline: `${deriveTimelineDays(Math.max(0, 1 - sufficiency) * 100, 100)} days`,
      ...statusFromRatio(sufficiency < 1 ? (1 - sufficiency) : 0),
    },
    {
      priority: 5,
      area: "CWSN accessibility closure",
      detail: "Remaining accessibility gap",
      signal: `${pct(cwsnGap)} gap`,
      owner: "Inclusive Education Officer",
      timeline: `${deriveTimelineDays(cwsnGap, 100)} days`,
      ...statusFromRatio(cwsnGap / 100),
    },
  ];

  const closurePlan = asInsightArray(insightsGuidance?.closure_plan, localClosurePlan);
  const actionQueue = asInsightArray(insightsGuidance?.action_queue, localActionQueue);

  const briefingCards = Array.isArray(insightsGuidance?.briefing_cards) ? insightsGuidance.briefing_cards : [];
  const briefingByKey = Object.fromEntries(briefingCards.map((c) => [c.key, c.description]));

  const defaultRoot = cardsToInsightLines(
    localDiagnosticSignals.map((s) => ({ title: s.title, body: s.description }))
  );
  const defaultRecs = toInsightLines(
    localClosurePlan.map((step) => `${step.title}: ${step.p}`)
  );
  const defaultActions = toInsightLines(
    localActionQueue.map((row) => `${row.area}: ${row.detail}`)
  );
  const aiContent = insightsGuidance?.markdown || insightsGuidance?.ai_markdown || "";

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const handleExportBriefing = () => {
    const lines = [
      "Classrooms & Toilets — AI Insights Briefing",
      `Generated: ${generatedAt}`,
      "",
      `AI Readiness: ${pct(aiReadiness)}`,
      `Classroom Health: ${pct(overview.classroom_health_index)}`,
      `Toilet Functional: ${pct(overview.toilet_functional_pct)}`,
      `Water Coverage: ${pct(overview.water_coverage_pct)}`,
      `Critical Exceptions: ${fmt(criticalExceptions)}`,
      "",
      "Executive Briefing",
      `- Strong baseline: ${pct(overview.classroom_health_index)} classroom health`,
      `- Water-linked risk: ${fmt(waterGap)} toilets without water`,
      `- Zero-toilet exception: ${fmt(zeroToiletTotal)} schools (${fmt(zeroBoys)} boys, ${fmt(zeroGirls)} girls)`,
      "",
      "Priority Actions",
      ...actionQueue.map(
        (a) => `${a.priority}. ${a.area} — ${a.signal} (${a.owner}, ${a.timeline})`
      ),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "classrooms-toilets-insights-briefing.txt";
    link.click();
    URL.revokeObjectURL(url);
    toast.success("Insights briefing exported");
  };

  return (
    <div className="ct-insights-tab" data-testid="tab-insights-content" id="ct-insights-overview">
      <section className="ct-hero-card ct-hero-card-insights">
        <div className="ct-hero-layout">
          <div>
            <div className="ct-insight-main">
              <div
                className="ct-insight-gauge"
                style={buildGaugeStyle(aiReadiness)}
                aria-label={`AI readiness score ${pct(aiReadiness)}`}
              >
                <div>
                  <strong>{pct(aiReadiness)}</strong>
                  <span>AI Readiness</span>
                </div>
              </div>
              <div className="ct-hero-copy">
                <h2>AI Insights Command Centre</h2>
                <p>{insightsHeroSummary}</p>
                <div className="ct-status-row">
                  <ClassroomsToiletsGuidanceChip provider={guidanceProvider} loading={guidanceLoading} />
                  <HeroStatusChip chipClass="ct-status-chip" variant="green" showChevron={false}>
                    Updated {generatedAt}
                  </HeroStatusChip>
                  <HeroStatusChip
                    chipClass="ct-status-chip"
                    variant="orange"
                    onClick={criticalThemes > 0 ? () => scrollTo("ct-insights-actions") : undefined}
                    ariaLabel={
                      criticalThemes > 0
                        ? `${criticalThemes} critical risk themes — view intervention actions`
                        : undefined
                    }
                  >
                    {criticalThemes} Critical Risk Themes
                  </HeroStatusChip>
                </div>
              </div>
            </div>
            <div className="ct-summary-note">
              <div className="ct-icon-box purple">
                <Sparkles size={22} />
              </div>
              <div>
                <b>Primary executive signal</b>
                <span>{primaryExecutiveSignal}</span>
              </div>
            </div>
          </div>

          <div className="ct-metric-grid ct-metric-grid-2" aria-label="Insight KPIs">
            <article className="ct-metric-card">
              <small>Classrooms Analyzed</small>
              <strong>{fmt(overview.total_classrooms)}</strong>
              <span className="ct-metric-sub">
                {overview.avg_classrooms_per_school?.toFixed(1) ?? "—"} average per school
              </span>
            </article>
            <article className="ct-metric-card">
              <small>Toilet Functional Rate</small>
              <strong>{pct(overview.toilet_functional_pct)}</strong>
              <span className="ct-metric-sub">{fmt(overview.total_functional)} functional toilets</span>
            </article>
            <article className="ct-metric-card warning">
              <small>Water Coverage</small>
              <strong>{pct(overview.water_coverage_pct)}</strong>
              <span className="ct-metric-sub">{fmt(waterGap)} toilets lack water</span>
            </article>
            <article className="ct-metric-card danger">
              <small>Critical Exceptions</small>
              <strong>{fmt(criticalExceptions)}</strong>
              <span className="ct-metric-sub">Toilet, water, handwash & repair gaps</span>
            </article>
          </div>
        </div>
      </section>

      <div className="ct-section-title">
        <div>
          <h2>Executive Briefing</h2>
          <p>
            Raw AI text has been converted into a scan-friendly briefing layer so officers can immediately
            understand what is good, what is risky and what to do next.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-insights-actions")}>
          View action queue →
        </button>
      </div>

      <section className="ct-briefing-grid">
        <BriefCard
          icon={Check}
          iconTone="green"
          title="Strong baseline"
          value={pct(overview.classroom_health_index)}
          sub="classroom health"
          description={
            briefingByKey.baseline ??
            `${fmt(overview.total_classrooms)} classrooms across ${fmt(overview.total_schools)} schools with ${pct(overview.repair_backlog_pct ?? 0)} repair backlog.`
          }
        />
        <BriefCard
          icon={Droplets}
          iconTone="orange"
          title="Water-linked risk"
          value={fmt(waterGap)}
          sub="toilets"
          description={
            briefingByKey.water ??
            "Functional toilets without reliable water supply are the biggest WASH usability gap."
          }
        />
        <BriefCard
          icon={AlertTriangle}
          iconTone="red"
          title="Zero-toilet exception"
          value={fmt(zeroToiletTotal)}
          sub="schools"
          description={
            briefingByKey.risk ??
            `${fmt(zeroBoys)} schools lack boys toilets and ${fmt(zeroGirls)} schools lack girls toilets, requiring immediate closure tracking.`
          }
        />
        <BriefCard
          icon={TrendingUp}
          iconTone="purple"
          title="Action focus"
          value={String(actionFocusDays)}
          sub="days"
          description={
            briefingByKey.action ??
            `Run a block-wise WASH closure sprint across ${fmt(criticalExceptions)} exceptions (${fmt(waterGap)} water gap, ${fmt(zeroToiletTotal)} zero-toilet schools).`
          }
        />
      </section>

      <div className="ct-section-title" id="ct-insights-root-cause">
        <div>
          <h2>Root Cause Signals</h2>
          <p>
            Signals are grouped by operational cause instead of displaying one long text block. This makes the
            insight more useful for review meetings.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={() => scrollTo("ct-insights-recommendations")}>
          Recommendations →
        </button>
      </div>

      <section className="ct-diagnostic-grid">
        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>Key Diagnostic Findings</h3>
              <p>Ranked by impact on school WASH readiness and child safety.</p>
            </div>
            <span className="ct-pill ct-pill-orange">Root cause view</span>
          </div>
          <div className="ct-signal-list">
            {diagnosticSignals.map((signal) => (
              <SignalRow key={signal.title} {...signal} />
            ))}
          </div>
        </article>

        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>AI Confidence & Evidence</h3>
              <p>Shows why the AI recommendation is reliable and what data was used.</p>
            </div>
            <span className="ct-pill ct-pill-green">{confidence}% confidence</span>
          </div>
          <div className="ct-confidence-card">
            <div className="ct-confidence-top">
              <div className="ct-mini-gauge" style={buildConfidenceGaugeStyle(confidence)}>
                <div>
                  <strong>{confidence}%</strong>
                  <span>Confidence</span>
                </div>
              </div>
              <div>
                <h3 className="ct-confidence-title">Evidence-backed insight</h3>
                <p className="ct-confidence-copy">
                  The insight combines classroom condition, toilet functionality, water availability, hygiene
                  coverage, gender access and risk exception data.
                </p>
              </div>
            </div>
            <div className="ct-evidence-grid">
              <div className="ct-evidence">
                <b>{fmt(overview.total_classrooms)}</b>
                <span>Classrooms assessed</span>
              </div>
              <div className="ct-evidence">
                <b>{fmt(overview.total_toilets)}</b>
                <span>Toilets monitored</span>
              </div>
              <div className="ct-evidence">
                <b>{fmt(handwashPoints)}</b>
                <span>Handwash points</span>
              </div>
              <div className="ct-evidence">
                <b>{fmt(overview.total_blocks ?? blockData?.length ?? 0)}</b>
                <span>Blocks compared</span>
              </div>
            </div>
            <div className="ct-ai-footer">
              <b>Insight freshness</b>
              <span>
                Generated {generatedAt}. Refresh weekly or after major data import to keep root-cause signals
                current.
              </span>
            </div>
          </div>
        </article>
      </section>

      <section className="ct-action-plan">
        <InterventionActionPlan
          aiContent={aiContent}
          defaultRoot={defaultRoot}
          defaultRecs={defaultRecs}
          defaultActions={defaultActions}
        />
      </section>

      <div className="ct-section-title" id="ct-insights-recommendations">
        <div>
          <h2>Recommendations and Priority Actions</h2>
          <p>
            Recommendations are converted into accountable, time-bound actions so the tab supports
            implementation, not just reporting.
          </p>
        </div>
        <button type="button" className="ct-section-action" onClick={handleExportBriefing}>
          Export briefing →
        </button>
      </div>

      <section className="ct-action-grid" id="ct-insights-actions">
        <article className="ct-panel ct-table-card">
          <div className="ct-table-head">
            <div>
              <h3>Officer Action Queue</h3>
              <p>Priority actions derived from AI insight and risk signals.</p>
            </div>
            <span className="ct-pill ct-pill-red">Action required</span>
          </div>
          <div className="ct-table-wrap">
            <table className="ct-infra-table" aria-label="AI generated action queue">
              <thead>
                <tr>
                  <th>Priority</th>
                  <th>Action Area</th>
                  <th>Signal</th>
                  <th>Owner</th>
                  <th>Timeline</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {actionQueue.map((row) => (
                  <tr key={row.priority}>
                    <td>{row.priority}</td>
                    <td>
                      <b>{row.area}</b>
                      <span>{row.detail}</span>
                    </td>
                    <td>{row.signal}</td>
                    <td>{row.owner}</td>
                    <td>{row.timeline}</td>
                    <td>
                      <span className={`ct-tag ${row.statusTone}`}>{row.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>

        <article className="ct-panel">
          <div className="ct-panel-head">
            <div>
              <h3>30-Day AI Closure Plan</h3>
              <p>Suggested operating rhythm for district and block officers.</p>
            </div>
            <span className="ct-pill">Sprint plan</span>
          </div>
          <div className="ct-plan-list">
            {closurePlan.map((step) => (
              <div className="ct-plan-item" key={step.num}>
                <div className="ct-plan-num">{step.num}</div>
                <div>
                  <h4>{step.title}</h4>
                  <p>{step.p}</p>
                </div>
              </div>
            ))}
            <div className="ct-ai-footer">
              <b>Closure target</b>
              <span>
                Complete {fmt(criticalExceptions)} tracked exceptions within {actionFocusDays} days at current
                risk ratio ({pct((criticalExceptions / totalSchools) * 100, 2)} of schools).
              </span>
            </div>
          </div>
        </article>
      </section>
    </div>
  );
};

export default ClassroomsToiletsInsightsTab;
